import { z } from "zod";

/**
 * Display labels the backend supplies alongside a widget's data — axis titles
 * and the per-field names used in hovers. Kept in one place because both are
 * "the backend telling us what to call things", and neither should ever be
 * invented by a renderer.
 *
 * Two key spellings are accepted on purpose, because both are live:
 *   x_label / y_label            — what the sentiment endpoints send
 *                                  ("Call duration (sec)")
 *   x_axis_label / y_axis_label  — the older spelling, e.g. grouped_bar's
 *                                  payload ("Duration (hours)")
 * Checking both means a renderer keeps its titles if the backend standardises
 * on either one, and neither side has to change first.
 */
/**
 * Two key spellings are accepted on purpose, because both are live:
 *   x_label / y_label            — what the sentiment endpoints send
 *                                  ("Call duration (sec)")
 *   x_axis_label / y_axis_label  — the older spelling, e.g. grouped_bar's
 *                                  payload ("Duration (hours)")
 */
const AxisLabelsSchema = z.object({
  x_label: z.string().optional(),
  y_label: z.string().optional(),
  x_axis_label: z.string().optional(),
  y_axis_label: z.string().optional(),
});

/**
 * `metadata.field_labels` — the backend's own name for each field in a data
 * row, meant for hovers: e.g. { x: "Call duration (sec)", count: "Calls with
 * this exact duration + interaction count" }. Note these are deliberately NOT
 * the axis titles: the same payload sends a short `field_labels.y` ("Scored
 * interactions in call") next to a longer `y_label` for the axis.
 *
 * Values are read as an open string map rather than a fixed key list, so a new
 * field the backend starts labelling shows up without a frontend change.
 */
const FieldLabelsSchema = z.object({
  field_labels: z.record(z.string(), z.string()).optional(),
});

export type FieldLabels = Record<string, string>;

/** The field-label map, or an empty map when the backend sends none. */
export const resolveFieldLabels = (metadata: unknown): FieldLabels =>
  FieldLabelsSchema.safeParse(metadata).data?.field_labels ?? {};

/**
 * A label for a full-width line (a tooltip caption, say), where there's room for
 * the backend's wording as-sent: its name for the field if it sent one, else the
 * caller's own fallback. Blank/whitespace values fall through.
 */
export const fieldLabel = (
  labels: FieldLabels,
  field: string,
  fallback: string,
): string => {
  const supplied = labels[field];
  return typeof supplied === "string" && supplied.trim().length > 0
    ? supplied.trim()
    : fallback;
};

/**
 * Longest label we'll put in a hover's left column. Past this the row stops
 * being a label and becomes a sentence — `field_labels` legitimately contains
 * explanatory prose ("Calls with this exact duration + interaction count"),
 * which belongs in documentation, not against a value.
 */
const MAX_HOVER_LABEL_CHARS = 22;

/** "Call duration (sec)" → "Call duration". */
const stripParenthetical = (text: string): string =>
  text
    .replace(/\s*\([^)]*\)\s*/g, " ")
    .replace(/\s+([,;:])/g, "$1")
    .replace(/\s+/g, " ")
    .trim();

/**
 * A compact hover row label. Takes the first candidate that still reads as a
 * label once its parenthetical is removed — so a short `field_labels` entry
 * wins, a long one gives way to the axis title, and prose gives way to the
 * caller's own short noun. Keeps hovers scannable without inventing wording:
 * every candidate is something the backend said.
 */
export const hoverLabel = (
  candidates: Array<string | null | undefined>,
  fallback: string,
): string => {
  for (const candidate of candidates) {
    if (typeof candidate !== "string") continue;
    const cleaned = stripParenthetical(candidate);
    if (cleaned.length > 0 && cleaned.length <= MAX_HOVER_LABEL_CHARS) {
      return cleaned;
    }
  }
  return fallback;
};

/**
 * The unit out of a label's parenthetical — "Call duration (sec)" → "sec" — so
 * stripping the parenthetical from the label doesn't lose it; it moves onto the
 * value instead ("118 sec").
 *
 * Only short, single-word parentheticals count as units: "(# scored
 * interactions)" is a description, not a unit, and is ignored.
 */
export const unitFrom = (...candidates: Array<string | null | undefined>): string | null => {
  for (const candidate of candidates) {
    if (typeof candidate !== "string") continue;
    const match = /\(([^)]+)\)/.exec(candidate);
    const unit = match?.[1]?.trim();
    if (unit && unit.length <= 6 && !/\s/.test(unit) && /[a-z]/i.test(unit)) {
      return unit;
    }
  }
  return null;
};

export type AxisTitles = { x: string | null; y: string | null };

const firstNonEmpty = (
  ...candidates: Array<string | undefined>
): string | null => {
  for (const candidate of candidates) {
    // Guard against a backend sending "" or "  " — an empty title should fall
    // through to the next source rather than blank the axis.
    if (typeof candidate === "string" && candidate.trim().length > 0) {
      return candidate.trim();
    }
  }
  return null;
};

/**
 * Resolve the axis titles for a chart.
 *
 * Precedence: the widget's own payload metadata wins over the dashboard's chart
 * spec (the payload is closer to the data being drawn), and `fallback` is used
 * only when neither side says anything. Pass `null` in `fallback` to leave an
 * axis untitled rather than invent a meaning for it.
 */
export const resolveAxisTitles = (
  metadata: unknown,
  spec: unknown,
  fallback: AxisTitles = { x: null, y: null },
): AxisTitles => {
  const meta = AxisLabelsSchema.safeParse(metadata).data ?? {};
  const specLabels = AxisLabelsSchema.safeParse(spec).data ?? {};

  return {
    x:
      firstNonEmpty(
        meta.x_label,
        meta.x_axis_label,
        specLabels.x_label,
        specLabels.x_axis_label,
      ) ?? fallback.x,
    y:
      firstNonEmpty(
        meta.y_label,
        meta.y_axis_label,
        specLabels.y_label,
        specLabels.y_axis_label,
      ) ?? fallback.y,
  };
};
